/**
 * Tracker 主类
 * SDK 核心入口
 */

import type {
  TrackerConfig,
  ResolvedTrackerConfig,
  CommonParams,
  PartialCommonParams,
  EventData,
  BusinessParams,
  TrackData,
  ClickEventParams,
  RequestFilter,
  RequestData,
} from '../types'
import type { ReportEventType } from '../types/event'
import type { ExposeMeta } from '../collectors/exposure'
import type { ScanMetrics } from '../modules/auto-exposure-scanner'
import { mergeConfig, validateConfig } from './config'
import { collectCommon, refreshDynamicFields } from '../collectors/common'
import { resolveRoutePath } from '../collectors/page'
import { Reporter } from '../reporter'
import { PageModule } from '../modules/page'
import { ClickModule } from '../modules/click'
import { ExposureModule } from '../modules/exposure'
import { NetworkModule } from '../modules/network'
import { AfterClickModule } from '../modules/after-click'
import { DynamicConfigManager } from './dynamic-config'
import { MOCK_DYNAMIC_CONFIG } from './mock-dynamic-config'
import { RouteChangeManager } from './route-change-manager'
import { shouldSample } from '../utils/sampling'

/**
 * Tracker 类
 */
export class Tracker {
  /** 配置 */
  private config: ResolvedTrackerConfig | null = null
  /** 公共参数 */
  private commonParams: CommonParams | null = null
  /** 报送器 */
  private reporter: Reporter | null = null
  /** 页面追踪模块 */
  private pageModule: PageModule | null = null
  /** 点击追踪模块 */
  private clickModule: ClickModule | null = null
  /** afterClick 追踪模块 */
  private afterClickModule: AfterClickModule | null = null
  /** 曝光追踪模块 */
  private exposureModule: ExposureModule | null = null
  /** 网络监控模块 */
  private networkModule: NetworkModule | null = null
  /** 路由变更管理器 */
  private routeChangeManager: RouteChangeManager | null = null
  /** 是否已初始化 */
  private isInitialized = false
  /** 动态配置管理器 */
  private dynamicConfigManager: DynamicConfigManager | null = null
  /** 是否启用了动态配置模式 */
  private dynamicConfigEnabled = false

  /**
   * 初始化 SDK
   * @param userConfig 用户配置
   */
  async init(userConfig: TrackerConfig): Promise<void> {
    if (this.isInitialized) {
      this.log('Tracker 已初始化，跳过')
      return
    }

    // 验证配置
    validateConfig(userConfig)

    // 合并配置
    this.config = mergeConfig(userConfig)

    // 采集公共参数（生成 sessionId，用于后续采样判断）
    this.commonParams = collectCommon(this.config)

    // 动态配置加载（提前于模块创建，用于 appStatus 和采样率门控）
    if (userConfig.mockDynamicConfig || this.config.configUrl) {
      this.dynamicConfigEnabled = true

      const configUrl = this.config.configUrl || '__mock__'
      this.dynamicConfigManager = new DynamicConfigManager(
        configUrl,
        this.config.appKey,
        this.config.appVersion,
        this.config.debug,
        this.config.enableSign,
        this.config.signSecret,
        this.config.containerAppKeys,
      )

      if (userConfig.mockDynamicConfig) {
        // 使用用户传入的 Mock 数据
        this.dynamicConfigManager.loadFromMock(userConfig.mockDynamicConfig)
      } else if (this.config.configUrl === 'mock') {
        // 使用内置 Mock 数据
        this.dynamicConfigManager.loadFromMock(MOCK_DYNAMIC_CONFIG)
      } else {
        // 远程加载
        this.dynamicConfigManager.load()
      }

      const dynamicConfig = this.dynamicConfigManager.getConfig()!
      // 无配置时，不进行后续初始化
      if (!dynamicConfig) {
        // 配置加载失败且无缓存 → 静默模式，不采集
        this.isInitialized = true
        const error = new Error('[XT-Tracker] Dynamic config load failed, SDK in silent mode')
        this.config.onConfigError?.(error)
        return
      }
      
      // 检查 appStatus：应用禁用时不初始化
      const appStatus = dynamicConfig.appStatus ?? 1
      if (appStatus === 0) {
        this.isInitialized = true
        this.log('应用已禁用(appStatus=0)，SDK 进入静默模式')
        return
      }

      // 检查全局采样率：未命中采样时不初始化
      const globalSamplingRate = dynamicConfig.globalSamplingRate ?? 100
      if (!shouldSample(this.commonParams.sessionId, globalSamplingRate)) {
        this.isInitialized = true
        this.log('全局采样未命中，SDK 进入静默模式', { globalSamplingRate })
        return
      }

      // 采样通过，记录到 commonParams
      this.commonParams.samplingRate = globalSamplingRate

      this.config.onConfigReady?.(dynamicConfig)
    }

    // 路由变更管理器（基础设施，先于所有模块初始化）
    this.routeChangeManager = new RouteChangeManager(this.config.debug)
    this.routeChangeManager.init()

    // 网络监控模块
    if (this.config.enableNetworkMonitor) {
      this.networkModule = new NetworkModule(
        {
          maxRequestsPerPage: this.config.networkMaxRequestsPerPage,
          maxPages: this.config.networkMaxPages,
          maxBodySize: this.config.networkMaxBodySize,
          maxResponseSize: this.config.networkMaxResponseSize,
          recordHeaders: this.config.networkRecordHeaders,
          recordBody: this.config.networkRecordBody,
          recordResponse: this.config.networkRecordResponse,
          excludeUrls: this.config.networkExcludeUrls,
          debug: this.config.debug,
        },
        this.routeChangeManager
      )
      this.networkModule.init()
    }

    // 创建 Reporter
    this.reporter = new Reporter({
      reportUrl: this.config.reportUrl,
      reportBatchUrl: this.config.reportBatchUrl,
      debug: this.config.debug,
      sampleRate: this.config.sampleRate,
      supportKeepAlive: this.config.supportKeepAlive,
    })

    this.isInitialized = true
    this.log('Tracker 初始化完成', this.config)

    // 初始化模块
    this.initModules()
  }

  /**
   * 初始化各功能模块
   */
  private initModules(): void {
    if (!this.config || !this.reporter || !this.commonParams || !this.routeChangeManager) return

    // 页面追踪模块
    if (this.config.enableAutoPageView) {
      this.pageModule = new PageModule(
        { enabled: true, debug: this.config.debug, routePathResolver: this.config.routePathResolver, referrerResolver: this.config.referrerResolver, pageReportMode: this.config.pageReportMode },
        this.reporter,
        this.commonParams,
        this.routeChangeManager,
        this.dynamicConfigManager ?? undefined,
        this.networkModule ?? undefined
      )
      this.pageModule.init()
    }

    // 点击追踪模块
    if (this.config.enableAutoClick) {
      // 先创建 AfterClickModule
      this.afterClickModule = new AfterClickModule(
        {
          enabled: true,
          debug: this.config.debug,
          routePathResolver: this.config.routePathResolver,
          referrerResolver: this.config.referrerResolver,
          reportUrl: this.config.reportUrl,
        },
        this.reporter,
        this.commonParams,
        this.dynamicConfigManager ?? undefined,
        this.pageModule ?? undefined
      )

      this.clickModule = new ClickModule(
        { enabled: true, debug: this.config.debug, routePathResolver: this.config.routePathResolver, referrerResolver: this.config.referrerResolver },
        this.reporter,
        this.commonParams,
        this.dynamicConfigManager ?? undefined,
        this.pageModule ?? undefined,
        this.afterClickModule
      )
      this.clickModule.init()
    }

    // 曝光追踪模块
    if (this.config.enableExposure) {
      this.exposureModule = new ExposureModule(
        {
          enabled: true,
          threshold: this.config.exposureThreshold,
          delay: this.config.exposureDelay,
          debug: this.config.debug,
          routePathResolver: this.config.routePathResolver,
          referrerResolver: this.config.referrerResolver,
          enableAutoExposure: this.config.enableAutoExposure,
          autoExposureMaxElements: this.config.autoExposureMaxElements,
          autoExposureScanRoot: this.config.autoExposureScanRoot,
        },
        this.reporter,
        this.commonParams,
        this.routeChangeManager,
        this.dynamicConfigManager ?? undefined,
        this.pageModule ?? undefined
      )
      this.exposureModule.init()
    }
  }

  /**
   * 检查动态配置是否阻止上报
   * @returns true 表示应跳过上报
   */
  private shouldSkipByDynamicConfig(): boolean {
    return this.dynamicConfigEnabled && !this.dynamicConfigManager?.isReady()
  }

  /**
   * 通用事件上报
   * @param eventType 事件类型
   * @param eventName 事件名称（业务定义）
   * @param eventParams 事件子参数
   * @param businessParams 业务参数（可选）
   */
  track(
    eventType: ReportEventType,
    eventName: string,
    eventCode: string,
    eventParams?: Record<string, unknown>,
    businessParams?: BusinessParams
  ): void {
    if (!this.isInitialized || !this.reporter || !this.commonParams) {
      this.log('Tracker not initialized')
      return
    }

    if (this.shouldSkipByDynamicConfig()) {
      this.log('Dynamic config not ready, skipping track')
      return
    }

    const common = refreshDynamicFields(this.commonParams)

    const event: EventData = {
      eventType,
      eventName,
      eventCode
    }

    // 根据事件类型填充子对象
    switch (eventType) {
      case 'openView':
      case 'closeView':
        event.page = {
          referrerPage: '',
          currentUrl: typeof location !== 'undefined' ? location.href : '',
          pageTitle: typeof document !== 'undefined' ? document.title : '',
          routePath: resolveRoutePath(this.config?.routePathResolver),
          ...eventParams,
        }
        break
      case 'click':
        event.click = eventParams as unknown as EventData['click']
        break
      case 'afterClick':
        event.afterClick = eventParams as unknown as EventData['afterClick']
        break
      case 'expose':
        event.expose = eventParams as unknown as EventData['expose']
        break
      case 'custom':
        event.custom = eventParams as unknown as EventData['custom']
        break
      case 'error':
        event.error = eventParams as unknown as EventData['error']
        break
      default:
        this.log('未知事件类型', eventType)
        return
    }

    const data: TrackData = {
      common,
      event,
      business: businessParams as BusinessParams,
    }

    this.reporter.report(data)
    this.log('Event tracked', { eventType, eventName })
  }

  /**
   * 手动上报页面打开事件
   * @param businessParams 业务参数（可选）
   */
  trackPageView(businessParams?: BusinessParams): void {
    if (!this.isInitialized || !this.reporter || !this.commonParams) {
      this.log('Tracker 未初始化')
      return
    }

    if (this.shouldSkipByDynamicConfig()) {
      this.log('动态配置未就绪，跳过 trackPageView')
      return
    }

    if (!this.pageModule) {
      this.log('PageModule 未初始化')
      return
    }

    // 调用 PageModule 的上报方法
    this.pageModule.reportPageView(businessParams ? { business: businessParams } : undefined)
  }

  /**
   * 手动上报点击事件
   */
  trackClick(
    params: Partial<ClickEventParams> & { eventName?: string },
    businessParams?: BusinessParams
  ): void {
    if (this.shouldSkipByDynamicConfig()) {
      this.log('动态配置未就绪，跳过 trackClick')
      return
    }

    if (!this.clickModule) {
      this.log('ClickModule 未初始化')
      return
    }

    this.clickModule.trackClick(params, businessParams)
  }

  /**
   * 设置用户 ID（登录后调用）
   * @param userId 用户 ID
   */
  setUserId(userId: string): void {
    if (!this.commonParams) {
      this.log('Tracker not initialized')
      return
    }

    this.commonParams.userId = userId
    this.updateModulesCommonParams()
    this.log('User ID set', userId)
  }

  /**
   * 补充/覆盖公共参数
   * @param partial 部分公共参数
   */
  setCommon(partial: PartialCommonParams): void {
    if (!this.commonParams) {
      this.log('Tracker not initialized')
      return
    }

    Object.assign(this.commonParams, partial)
    this.updateModulesCommonParams()
    this.log('Common params updated', partial)
  }

  /**
   * 更新各模块的公共参数引用
   */
  private updateModulesCommonParams(): void {
    if (!this.commonParams) return

    this.pageModule?.updateCommonParams(this.commonParams)
    this.clickModule?.updateCommonParams(this.commonParams)
    this.exposureModule?.updateCommonParams(this.commonParams)
    this.afterClickModule?.updateCommonParams(this.commonParams)
  }

  /**
   * 注册元素曝光观察
   * @param element 要观察的元素
   * @param meta 元数据（可选）
   * @param businessParams 业务参数（可选）
   */
  observeExposure(
    element: Element,
    meta?: ExposeMeta,
    businessParams?: BusinessParams
  ): number | undefined {
    if (!this.exposureModule) {
      this.log('Exposure module not enabled')
      return undefined
    }

    if (this.shouldSkipByDynamicConfig()) {
      this.log('Dynamic config not ready, skipping observeExposure')
      return undefined
    }

    return this.exposureModule.observe(element, meta, businessParams)
  }

  /**
   * 停止元素曝光观察
   * @param element 元素
   */
  unobserveExposure(element: Element): void {
    this.exposureModule?.unobserve(element)
  }

  /**
   * 获取当前公共参数
   */
  getCommonParams(): CommonParams | null {
    return this.commonParams ? { ...this.commonParams } : null
  }

  /**
   * 获取当前配置
   */
  getConfig(): ResolvedTrackerConfig | null {
    return this.config ? { ...this.config } : null
  }

  /**
   * 获取动态配置管理器（供高级用户使用）
   */
  getDynamicConfigManager(): DynamicConfigManager | null {
    return this.dynamicConfigManager
  }

  /**
   * 获取自动曝光扫描性能指标
   */
  getAutoExposureMetrics(): ScanMetrics | null {
    return this.exposureModule?.getAutoScanMetrics() ?? null
  }

  // ── 网络监控相关 API ──

  /**
   * 获取指定页面的请求列表
   * @param pageUrl 页面 URL（完整 URL，含查询参数）
   * @param filter 过滤条件（可选）
   */
  getRequestsForPage(pageUrl: string, filter?: RequestFilter): RequestData[] {
    if (!this.networkModule) {
      this.log('Network module not enabled')
      return []
    }
    return this.networkModule.getRequestsForPage(pageUrl, filter)
  }

  /**
   * 获取当前页面的请求列表
   * @param filter 过滤条件（可选）
   */
  getCurrentPageRequests(filter?: RequestFilter): RequestData[] {
    if (!this.networkModule) {
      this.log('Network module not enabled')
      return []
    }
    return this.networkModule.getCurrentPageRequests(filter)
  }

  /**
   * 清除指定页面的请求记录
   * @param pageUrl 页面 URL
   */
  clearPageRequests(pageUrl: string): void {
    if (!this.networkModule) {
      this.log('Network module not enabled')
      return
    }
    this.networkModule.clearPageRequests(pageUrl)
  }

  /**
   * 清除所有请求记录
   */
  clearAllRequests(): void {
    if (!this.networkModule) {
      this.log('Network module not enabled')
      return
    }
    this.networkModule.clearAllRequests()
  }

  /**
   * 销毁 SDK
   */
  destroy(): void {
    if (!this.isInitialized) return

    // 销毁模块（模块内会 unsubscribe 路由监听）
    this.pageModule?.destroy()
    this.clickModule?.destroy()
    this.exposureModule?.destroy()
    this.networkModule?.destroy()

    // 最后销毁路由变更管理器（恢复原始 history 方法）
    this.routeChangeManager?.destroy()

    // 清理状态
    this.pageModule = null
    this.clickModule = null
    this.afterClickModule = null
    this.exposureModule = null
    this.networkModule = null
    this.routeChangeManager = null
    this.reporter = null
    this.commonParams = null
    this.config = null
    this.dynamicConfigManager = null
    this.dynamicConfigEnabled = false
    this.isInitialized = false

    this.log('Tracker 已销毁')
  }

  /**
   * 调试
   */
  private log(message: string, data?: unknown): void {
    if (this.config?.debug) {
      console.log('%c[Tracker 调试] %c ' + message, 'background: #F56C6C; color: #fff;', 'color: inherit;', data)
    }
  }
}
